import React, { memo, useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { AdvancedTooltip, Dropdown, SlideUp, Text } from 'common-components'
import { useDropdownState } from 'hooks/useDropdownState'
import { useResponsive } from 'hooks/useResponsive'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { OVERLAY_MAX_WIDTH } from 'consts/sidebar'
import { editorThunks } from 'store/features/editor/thunks'
import { dispatch } from 'store'
import { IOptionDropdownProps } from './types'
import { useEventListener } from 'hooks'
import { useScrollableElement } from '../../../context'
import DropdownContent from './DropdownContent'

const OptionDropdown: React.FC<IOptionDropdownProps> = ({
  label,
  options,
  selectedValue,
  onSelect,
  disabled = false,
  disabledTooltipText,
  className,
  forceBottom = false
}) => {
  const { scrollableElementRef } = useScrollableElement()
  const { isOpen, closeDropdown, toggleDropdown } = useDropdownState(
    () => dispatch(editorThunks.setIsMobileInnerSliderOpen(true)),
    () => dispatch(editorThunks.setIsMobileInnerSliderOpen(false))
  )
  const [wrapperEl, setWrapperEl] = useState<HTMLDivElement | null>(null)

  const isTablet = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)

  const handleSelect = useCallback(
    (value: string | number) => {
      onSelect(value)
      closeDropdown()
    },
    [onSelect, closeDropdown]
  )

  useEventListener(
    'scroll',
    closeDropdown,
    !isTablet ? scrollableElementRef.current : null,
    true
  )

  const selectedOption = options.find(option => option.value === selectedValue)
  const displayValue = selectedOption?.label || label
  const isDisabled = disabled || options.length === 0

  if (isTablet && isOpen && !isDisabled) {
    return createPortal(
      <SlideUp isOpen={isOpen} onClose={closeDropdown}>
        <DropdownContent
          options={options}
          selectedValue={selectedValue}
          onSelect={handleSelect}
        />
      </SlideUp>,
      document.body
    )
  }

  return (
    <>
      <div ref={setWrapperEl} className={className}>
        <Dropdown
          headerContent={
            <Text type="h2" color="secondaryDark">
              {displayValue}
            </Text>
          }
          mainContent={
            <DropdownContent
              options={options}
              selectedValue={selectedValue}
              onSelect={handleSelect}
              hasFixedHeight={forceBottom}
            />
          }
          isOpen={isOpen}
          toggleDropdown={toggleDropdown}
          closeDropdown={closeDropdown}
          forceBottom={forceBottom}
          disabled={isDisabled}
        />
      </div>
      {isDisabled && disabledTooltipText ? (
        <AdvancedTooltip
          tooltipText={disabledTooltipText}
          btnRef={{ current: wrapperEl }}
          showMobileTooltip
          isToggledByClick={false}
          maxWidth={OVERLAY_MAX_WIDTH}
        />
      ) : null}
    </>
  )
}

export default memo(OptionDropdown)
